import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ArrowUpRight, ChevronDown } from "lucide-react";
import { NAV_LINKS } from "@/data/content";
import { PLATFORM_CATEGORIES } from "@/data/platform";

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [platformOpen, setPlatformOpen] = useState(false);
  const { pathname } = useLocation();
  const isHome = pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Section links: smooth-scroll on home, route back home elsewhere
  const sectionLink = (id) =>
    isHome ? (
      <a
        href={`#${id}`}
        data-testid={`nav-${id}`}
        className="group relative font-body text-sm text-white/60 hover:text-white transition-colors duration-300"
      >
        {NAV_LINKS.find((l) => l.href === `#${id}`)?.label}
        <span className="absolute -bottom-1.5 left-0 h-px w-0 bg-[#e01e37] transition-[width] duration-300 group-hover:w-full" />
      </a>
    ) : (
      <Link
        to={{ pathname: "/", hash: `#${id}` }}
        data-testid={`nav-${id}`}
        className="group relative font-body text-sm text-white/60 hover:text-white transition-colors duration-300"
      >
        {NAV_LINKS.find((l) => l.href === `#${id}`)?.label}
        <span className="absolute -bottom-1.5 left-0 h-px w-0 bg-[#e01e37] transition-[width] duration-300 group-hover:w-full" />
      </Link>
    );

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
        scrolled || !isHome
          ? "bg-[#05060a]/80 backdrop-blur-xl border-b border-white/10"
          : "bg-transparent border-b border-transparent"
      }`}
      data-testid="site-header"
    >
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
        <div className="flex h-16 lg:h-20 items-center justify-between">
          {/* Logo */}
          <Link to="/" data-testid="logo-link" className="group flex items-center gap-2">
            <span className="font-display text-xl lg:text-2xl font-extrabold tracking-tight text-white">
              Cash<span className="text-[#e01e37]">FUX</span>
            </span>
            <span className="hidden sm:inline-block font-mono text-[10px] uppercase tracking-[0.25em] text-white/40 border border-white/10 px-2 py-0.5">
              BaaS
            </span>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-9">
            {/* Plattform with mega-menu */}
            <div
              className="relative"
              onMouseEnter={() => setPlatformOpen(true)}
              onMouseLeave={() => setPlatformOpen(false)}
            >
              <Link
                to="/plattform"
                data-testid="nav-plattform"
                className="group flex items-center gap-1.5 font-body text-sm text-white/60 hover:text-white transition-colors duration-300"
              >
                Plattform
                <ChevronDown
                  className={`h-3.5 w-3.5 transition-transform duration-300 ${platformOpen ? "rotate-180" : ""}`}
                />
              </Link>

              <AnimatePresence>
                {platformOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 8 }}
                    transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
                    className="absolute left-1/2 top-full -translate-x-1/2 pt-4"
                    data-testid="platform-megamenu"
                  >
                    <div className="w-[760px] border border-white/10 bg-[#0a0c12]/95 backdrop-blur-xl accent-glow">
                      <div className="grid grid-cols-2 gap-px bg-white/10">
                        {PLATFORM_CATEGORIES.map((cat) => (
                          <div key={cat.name} className="bg-[#0a0c12] p-5">
                            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#e01e37]">
                              {cat.name}
                            </span>
                            <ul className="mt-3 space-y-1">
                              {cat.products.map((p) => (
                                <li key={p.slug}>
                                  <Link
                                    to={`/plattform/${p.slug}`}
                                    data-testid={`megamenu-${p.slug}`}
                                    className="group flex items-center gap-2.5 py-1.5 text-sm text-white/65 hover:text-white transition-colors duration-200"
                                  >
                                    <p.icon className="h-3.5 w-3.5 text-white/35 group-hover:text-[#e01e37] transition-colors duration-200" strokeWidth={1.5} />
                                    {p.name}
                                  </Link>
                                </li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                      <Link
                        to="/plattform"
                        data-testid="megamenu-overview"
                        className="flex items-center justify-between border-t border-white/10 px-5 py-4 text-sm text-white hover:bg-white/5 transition-colors"
                      >
                        <span className="font-medium">Alle 18 Module ansehen</span>
                        <ArrowUpRight className="h-4 w-4 text-[#e01e37]" />
                      </Link>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {sectionLink("funktionen")}
            {sectionLink("unternehmen")}
            {sectionLink("api")}
            {sectionLink("kontakt")}
          </nav>

          <div className="hidden items-center gap-5 lg:flex">
            <Link
              to="/login"
              data-testid="header-login"
              className="font-body text-sm text-white/55 transition-colors hover:text-white"
            >
              Login
            </Link>
            {isHome ? (
              <a
                href="#kontakt"
                data-testid="header-cta"
                className="group inline-flex items-center gap-2 bg-[#e01e37] px-5 py-2.5 text-sm font-medium text-white transition-[background-color] duration-300 hover:bg-[#c91830]"
              >
                Demo anfragen
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </a>
            ) : (
              <a
                href="mailto:kontakt@cashfux.com"
                data-testid="header-cta"
                className="group inline-flex items-center gap-2 bg-[#e01e37] px-5 py-2.5 text-sm font-medium text-white transition-[background-color] duration-300 hover:bg-[#c91830]"
              >
                Demo anfragen
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </a>
            )}
          </div>

          {/* Mobile toggle */}
          <button
            onClick={() => setOpen((v) => !v)}
            data-testid="mobile-menu-toggle"
            className="lg:hidden inline-flex h-11 w-11 items-center justify-center border border-white/10 text-white"
            aria-label="Menü"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden overflow-hidden bg-[#05060a]/95 backdrop-blur-xl border-b border-white/10"
            data-testid="mobile-menu"
          >
            <nav className="flex max-h-[70vh] flex-col overflow-y-auto px-6 py-6">
              <Link
                to="/plattform"
                onClick={() => setOpen(false)}
                data-testid="mobile-nav-plattform"
                className="border-b border-white/5 py-4 font-display text-lg text-white/80 hover:text-white transition-colors"
              >
                Plattform
              </Link>
              {["funktionen", "unternehmen", "api", "kontakt"].map((id) => (
                <Link
                  key={id}
                  to={isHome ? { hash: `#${id}` } : { pathname: "/", hash: `#${id}` }}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-${id}`}
                  className="border-b border-white/5 py-4 font-display text-lg text-white/80 hover:text-white transition-colors"
                >
                  {NAV_LINKS.find((l) => l.href === `#${id}`)?.label}
                </Link>
              ))}
              <a
                href="mailto:kontakt@cashfux.com"
                onClick={() => setOpen(false)}
                data-testid="mobile-header-cta"
                className="mt-6 inline-flex items-center justify-center gap-2 bg-[#e01e37] px-5 py-3 text-sm font-medium text-white"
              >
                Demo anfragen
                <ArrowUpRight className="h-4 w-4" />
              </a>
              <Link
                to="/login"
                onClick={() => setOpen(false)}
                data-testid="mobile-header-login"
                className="mt-3 inline-flex items-center justify-center border border-white/10 px-5 py-3 text-sm font-medium text-white/70"
              >
                Zum Login
              </Link>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
